import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence, useInView } from 'framer-motion'
import {
  AlertTriangle,
  ChevronDown,
  ArrowRight,
  CheckCircle,
  Clock,
  Repeat,
  Users,
  Eye,
  Zap,
  Shield,
  Building2,
  Briefcase,
  Scale,
  Megaphone,
  Activity,
  TrendingDown,
  Lock,
  EyeOff,
  FileText,
} from 'lucide-react'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import { usePageMeta } from '../hooks/usePageMeta'

// ─── Hero Visualization ───────────────────────────────────────────────────────

function HeroVisualization() {
  const engagements = [
    { name: 'ERP Implementation', partner: 'Meridian Consulting', status: 'at-risk', health: 44, trend: '↓' },
    { name: 'Legal Due Diligence', partner: 'Hartwell & Partners', status: 'healthy', health: 83, trend: '↑' },
    { name: 'Digital Campaign Q3', partner: 'Collective Agency', status: 'attention', health: 61, trend: '↓' },
    { name: 'Cloud Migration', partner: 'Vantage Group', status: 'healthy', health: 77, trend: '→' },
  ]

  const alerts = [
    { text: 'ERP: same blocker raised 4 consecutive weeks', type: 'alert', delay: 2.1 },
    { text: 'Digital Campaign: key stakeholder absent last 3 meetings', type: 'warn', delay: 2.45 },
  ]

  return (
    <div className="relative w-full max-w-lg mx-auto lg:mx-0">
      <div className="absolute -inset-6 bg-gradient-to-br from-primary-500/10 via-accent-500/8 to-transparent rounded-3xl blur-2xl pointer-events-none" />
      <div className="absolute -bottom-4 left-1/4 right-1/4 h-16 bg-accent-500/15 rounded-full blur-2xl pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-premium border border-white/60 p-6 space-y-5"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400 font-mono uppercase tracking-widest">External Engagements</p>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            <span className="text-xs text-gray-400 font-mono">monitoring</span>
          </div>
        </div>

        {/* Engagement rows */}
        <div className="space-y-2">
          {engagements.map((eng, i) => (
            <motion.div
              key={eng.name}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + i * 0.15, duration: 0.4 }}
              className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3"
            >
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  eng.status === 'at-risk'
                    ? 'bg-error'
                    : eng.status === 'attention'
                    ? 'bg-warning'
                    : 'bg-success'
                }`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate">{eng.name}</p>
                <p className="text-[10px] text-gray-400">{eng.partner}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p
                  className={`text-sm font-bold ${
                    eng.health < 55 ? 'text-error' : eng.health < 70 ? 'text-warning' : 'text-success'
                  }`}
                >
                  {eng.health}
                </p>
                <p
                  className={`text-[10px] font-medium ${
                    eng.trend === '↓'
                      ? 'text-error'
                      : eng.trend === '↑'
                      ? 'text-success'
                      : 'text-gray-400'
                  }`}
                >
                  {eng.trend}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Alert pills */}
        <div className="space-y-2">
          {alerts.map((alert, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: alert.delay }}
              className={`flex items-start gap-2 px-3 py-2.5 rounded-xl text-xs ${
                alert.type === 'alert'
                  ? 'bg-error/10 text-error border border-error/20'
                  : 'bg-warning/10 text-warning border border-warning/20'
              }`}
            >
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{alert.text}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 3.0, duration: 0.4 }}
        className="absolute -top-4 -right-4 badge-accent text-sm px-4 py-2 shadow-glow-sm animate-pulse-slow"
      >
        Rithmo · Monitoring Active
      </motion.div>
    </div>
  )
}

// ─── Warning Signal Timeline ──────────────────────────────────────────────────

const warningSignals = [
  { week: 'Week 3', text: 'The same issue gets discussed again', level: 'warn' },
  { week: 'Week 5', text: 'A decision from last month gets reopened', level: 'warn' },
  { week: 'Week 7', text: 'Commitments remain unresolved meeting after meeting', level: 'warn' },
  { week: 'Week 9', text: 'Key stakeholders start missing calls', level: 'error' },
  { week: 'Week 11', text: 'Momentum visibly slows', level: 'error' },
  { week: 'Week 13', text: 'Deadline slips. Everyone acts surprised.', level: 'error' },
]

function WarningSignalTimeline() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  const [visibleCount, setVisibleCount] = useState(0)

  useEffect(() => {
    if (!isInView) {
      setVisibleCount(0)
      return
    }
    let i = 0
    const timer = setInterval(() => {
      i++
      setVisibleCount(i)
      if (i >= warningSignals.length) clearInterval(timer)
    }, 600)
    return () => clearInterval(timer)
  }, [isInView])

  return (
    <div ref={ref} className="relative max-w-sm mx-auto">
      {/* Vertical line */}
      <div className="absolute left-[9px] top-3 bottom-3 w-px bg-white/15" />

      <div className="space-y-5">
        {warningSignals.map((signal, i) => {
          const isVisible = i < visibleCount
          const isLast = i === warningSignals.length - 1
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: isVisible ? 1 : 0.18, x: 0 }}
              transition={{ duration: 0.4 }}
              className="flex items-start gap-4 pl-7 relative"
            >
              <div
                className={`absolute left-0 top-1.5 w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                  isLast && isVisible
                    ? 'bg-error shadow-[0_0_12px_rgba(239,68,68,0.6)]'
                    : signal.level === 'error' && isVisible
                    ? 'bg-error/80'
                    : signal.level === 'warn' && isVisible
                    ? 'bg-warning/80'
                    : 'bg-white/10'
                }`}
              >
                {isLast && isVisible ? (
                  <AlertTriangle className="w-2.5 h-2.5 text-white" />
                ) : (
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      signal.level === 'error' ? 'bg-white' : 'bg-white/70'
                    }`}
                  />
                )}
              </div>
              <div>
                <p
                  className={`text-[10px] font-mono mb-0.5 ${
                    isVisible ? 'text-white/40' : 'text-white/15'
                  }`}
                >
                  {signal.week}
                </p>
                <p
                  className={`text-sm leading-snug transition-colors duration-300 ${
                    isVisible
                      ? isLast
                        ? 'text-error font-semibold'
                        : signal.level === 'error'
                        ? 'text-red-300'
                        : 'text-yellow-200'
                      : 'text-white/15'
                  }`}
                >
                  {signal.text}
                </p>
              </div>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Live Engagement Card ─────────────────────────────────────────────────────

const wait = (ms) => new Promise((res) => setTimeout(res, ms))

function LiveEngagementCard() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.5 })
  const [phase, setPhase] = useState(0)

  useEffect(() => {
    if (!isInView) return
    let cancelled = false
    const run = async () => {
      while (!cancelled) {
        setPhase(0)
        await wait(4500)
        if (cancelled) break
        setPhase(1)
        await wait(4000)
        if (cancelled) break
        setPhase(2)
        await wait(5500)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [isInView])

  const topics = [
    { topic: 'Data migration ownership', recurring: true },
    { topic: 'Integration timeline review', recurring: false },
    { topic: 'API access permissions', recurring: true },
  ]

  return (
    <div ref={ref} className="rounded-2xl border border-accent-500/20 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 bg-dark-600/90">
        <div className="flex items-center gap-2">
          <motion.div
            className="w-2 h-2 rounded-full bg-accent-400"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
          <span className="text-xs font-semibold text-white">Rithmo · ERP Implementation Review</span>
        </div>
        <span className="text-[10px] text-dark-200 font-mono">Week 8 · Live</span>
      </div>

      <div className="bg-gradient-to-br from-primary-50/60 to-accent-50/40 p-5 space-y-4">
        <div>
          <p className="text-[10px] text-gray-400 font-mono uppercase tracking-wider mb-2">
            Topics This Meeting
          </p>
          <div className="space-y-2">
            {topics.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                {item.recurring ? (
                  <Repeat className="w-3 h-3 text-warning flex-shrink-0" />
                ) : (
                  <CheckCircle className="w-3 h-3 text-success flex-shrink-0" />
                )}
                <span className="text-xs text-gray-700 flex-1">{item.topic}</span>
                {item.recurring && (
                  <span className="text-[10px] text-warning font-semibold flex-shrink-0">3rd time</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <motion.div
          animate={{ opacity: phase >= 1 ? 1 : 0.25 }}
          transition={{ duration: 0.5 }}
          className="bg-dark-700 rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2.5">
            <Zap className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" />
            <span className="text-[10px] font-semibold text-accent-400 uppercase tracking-wider">
              Rithmo · Risk Detected
            </span>
          </div>
          <div className="relative h-[54px]">
            <motion.p
              animate={{ opacity: phase === 0 ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 text-sm text-dark-300 leading-snug"
            >
              Monitoring discussion patterns across 8 weeks of ERP reviews...
            </motion.p>
            <motion.p
              animate={{ opacity: phase === 1 ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 text-sm text-white leading-snug"
            >
              Data migration ownership raised 3 consecutive weeks without resolution.
              This is blocking the path to go live.
            </motion.p>
            <motion.p
              animate={{ opacity: phase === 2 ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 text-sm text-success leading-snug"
            >
              Leader briefed before next steering committee. Ownership resolved in advance.
              Risk avoided.
            </motion.p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

// ─── FAQ Item ─────────────────────────────────────────────────────────────────

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
      >
        <span className="font-semibold text-gray-900">{question}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-gray-600 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Page Data ────────────────────────────────────────────────────────────────

const warningSigns = [
  {
    icon: <Repeat className="w-5 h-5" />,
    title: 'The same issue keeps getting discussed',
    description:
      "When a topic surfaces meeting after meeting without resolution, it's a signal that something is stuck. It rarely gets louder before it becomes a delay.",
  },
  {
    icon: <TrendingDown className="w-5 h-5" />,
    title: 'Decisions get reopened',
    description:
      'Revisiting settled decisions is one of the clearest signs of misalignment or missing authority. Every reopened decision costs time that was supposed to be in the past.',
  },
  {
    icon: <AlertTriangle className="w-5 h-5" />,
    title: 'Commitments remain unresolved',
    description:
      "Action items that carry week after week without owners or updates are project debt accumulating quietly. By the time it shows in results, it's expensive to unwind.",
  },
  {
    icon: <Users className="w-5 h-5" />,
    title: 'Key stakeholders stop showing up',
    description:
      'Disengagement is often the first visible symptom of a project losing momentum. When the people who matter most start missing calls, the project is already drifting.',
  },
]

const capabilities = [
  {
    title: 'Know Which Engagements Need Attention',
    description:
      "Most leaders don't have time to attend every project review, steering committee, vendor check-in, or status meeting. Rithmo attends them continuously, surfacing which engagements are healthy, which are slowing, and which need your direct involvement.",
    icon: <Eye className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
          Engagement Health · Week of Jun 3
        </p>
        {[
          { name: 'ERP Implementation', partner: 'Meridian Consulting', status: 'at-risk', health: 44 },
          { name: 'Legal Due Diligence', partner: 'Hartwell & Partners', status: 'healthy', health: 83 },
          { name: 'Digital Campaign', partner: 'Collective Agency', status: 'attention', health: 61 },
          { name: 'Cloud Migration', partner: 'Vantage Group', status: 'healthy', health: 77 },
        ].map((eng) => (
          <div key={eng.name} className="flex items-center gap-3">
            <div
              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                eng.status === 'at-risk'
                  ? 'bg-error'
                  : eng.status === 'attention'
                  ? 'bg-warning'
                  : 'bg-success'
              }`}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-800 font-medium truncate">{eng.name}</p>
              <p className="text-xs text-gray-400">{eng.partner}</p>
            </div>
            <span
              className={`text-sm font-bold flex-shrink-0 ${
                eng.health < 55 ? 'text-error' : eng.health < 70 ? 'text-warning' : 'text-success'
              }`}
            >
              {eng.health}
            </span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-error text-xs">1 engagement needs immediate attention</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Surface Risks Before They Become Delays',
    description:
      "Rithmo tracks what's repeating, what's unresolved, and who's disengaging across every meeting in every engagement. It connects the pattern across weeks so you see the risk accumulating before a missed deadline lands on your desk.",
    icon: <AlertTriangle className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
          Recurring Blockers · ERP Implementation
        </p>
        {[
          { topic: 'Data migration ownership', weeks: 4 },
          { topic: 'API access approval', weeks: 3 },
          { topic: 'Go live date confirmation', weeks: 2 },
        ].map((row) => (
          <div key={row.topic} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Repeat className="w-3.5 h-3.5 text-warning flex-shrink-0" />
              <span className="text-sm text-gray-700">{row.topic}</span>
            </div>
            <span className="text-xs font-semibold text-warning flex-shrink-0">
              {row.weeks} weeks
            </span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-warning text-xs">3 unresolved blockers: escalation recommended</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Track Commitments Across Every Meeting',
    description:
      'Action items and commitments made in meetings are only valuable if they close. Rithmo tracks what gets committed and by whom, following up across meetings so nothing falls through the gap between the call and the delivery.',
    icon: <CheckCircle className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
          Open Commitments · Last 30 Days
        </p>
        {[
          { item: 'Revised project timeline', owner: 'Meridian Consulting PM', age: '18 days', overdue: true },
          { item: 'Security audit report', owner: 'Vantage Group', age: '7 days', overdue: false },
          { item: 'Final contract redline', owner: 'Hartwell & Partners', age: '22 days', overdue: true },
        ].map((c) => (
          <div key={c.item} className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-700 flex-1">{c.item}</span>
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-gray-500">{c.owner}</p>
              <p className={`text-xs font-semibold ${c.overdue ? 'text-error' : 'text-gray-400'}`}>
                {c.age}
              </p>
            </div>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-error text-xs">2 of 3 commitments overdue</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Stay Informed Without Attending Every Meeting',
    description:
      'Rithmo joins your external meetings and delivers structured briefings automatically. Executives gain visibility. Project owners gain alignment. Partners stay coordinated. Everyone spends less time chasing status.',
    icon: <Activity className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
          Executive Briefing · ERP Implementation
        </p>
        <div className="space-y-2.5">
          {[
            {
              label: 'Momentum',
              value: 'Declining',
              note: 'Down 18pts over 4 weeks',
              color: 'text-error',
            },
            {
              label: 'Open blockers',
              value: '3 unresolved',
              note: 'Oldest: 4 weeks',
              color: 'text-warning',
            },
            {
              label: 'Stakeholder attendance',
              value: 'Dropping',
              note: 'PM missed last 2 meetings',
              color: 'text-warning',
            },
            {
              label: 'Delivery risk',
              value: 'High',
              note: 'Intervention recommended',
              color: 'text-error',
            },
          ].map((row) => (
            <div key={row.label} className="flex items-start justify-between gap-3">
              <span className="text-sm text-gray-500 flex-shrink-0">{row.label}</span>
              <div className="text-right">
                <p className={`text-sm font-semibold ${row.color}`}>{row.value}</p>
                <p className="text-xs text-gray-400">{row.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
]

const verticals = [
  {
    icon: <Briefcase className="w-6 h-6" />,
    title: 'Consulting Engagements',
    description:
      'Understand project momentum across consulting relationships. Know whether your consulting partner is delivering before the invoice arrives.',
  },
  {
    icon: <Activity className="w-6 h-6" />,
    title: 'Implementation Projects',
    description:
      "Stay ahead of risks during software deployments and digital transformations. Surface the blockers your implementation partner isn't escalating.",
  },
  {
    icon: <Scale className="w-6 h-6" />,
    title: 'Legal & Compliance Initiatives',
    description:
      "Track decisions, commitments, and unresolved issues across legal matters. Know what's moving and what's stalled before the next billing cycle.",
  },
  {
    icon: <Megaphone className="w-6 h-6" />,
    title: 'Agencies & Strategic Partners',
    description:
      'Gain visibility into ongoing campaigns, initiatives, and external programs. Catch early signs of drift before they show up in deliverables.',
  },
]

const outcomes = [
  'Earlier visibility into delivery risk',
  'Fewer surprises at project milestones',
  'Recurring blockers surfaced before they compound',
  'Stakeholder disengagement caught early',
  'Reopened decisions flagged before they stall the team',
  'Unresolved commitments tracked automatically across every meeting',
]

const workflowSteps = [
  { icon: <Users className="w-5 h-5" />, label: 'External Meetings Continue as Normal' },
  { icon: <Eye className="w-5 h-5" />, label: 'Rithmo Monitors Every Meeting' },
  { icon: <Activity className="w-5 h-5" />, label: 'Blockers, Patterns & Risks Tracked' },
  { icon: <Zap className="w-5 h-5" />, label: 'Leaders Briefed Automatically' },
  { icon: <CheckCircle className="w-5 h-5" />, label: 'Intervention Before It Becomes a Problem' },
]

const faqs = [
  {
    question: 'Does Rithmo replace the project meetings I already run?',
    answer:
      'No. Rithmo works alongside your existing meeting structure. It joins the meetings you already have with your external partners and surfaces intelligence from them. No workflow changes required.',
  },
  {
    question: 'Do my external partners need to do anything?',
    answer:
      "External participants simply see Rithmo as a meeting attendee, the same way they'd see a note taker or a Chief of Staff sitting in. No setup, no accounts, no additional tools required on their side.",
  },
  {
    question: 'How is this different from the notes or summaries I already get?',
    answer:
      "Meeting summaries tell you what happened in a single meeting. Rithmo connects patterns across weeks and multiple meetings: the same blocker appearing four weeks in a row, a commitment that was made but never delivered, a key stakeholder who started missing calls. That's the intelligence that catches risk before it becomes a deadline slip.",
  },
  {
    question: 'Which meeting platforms does Rithmo support?',
    answer:
      'Rithmo currently supports Zoom. Additional platform support is on the roadmap.',
  },
  {
    question: 'Can I use Rithmo to monitor just one or two external engagements, or does it require org-wide deployment?',
    answer:
      'Rithmo works at whatever scope you need. You can deploy it on a single external engagement or across your entire portfolio of external relationships.',
  },
  {
    question: 'Is Rithmo designed for monitoring employees or vendors?',
    answer:
      'Rithmo is designed to give leaders visibility into project and engagement health, not to surveil individuals. The intelligence it surfaces is about delivery risk, commitment patterns, and meeting quality, not individual performance.',
  },
]

// ─── Page ─────────────────────────────────────────────────────────────────────

function ExternalEngagementsPage() {
  usePageMeta({
    title: 'Rithmo for External Engagements: Project Risk Visibility Before Deadlines Slip',
    description:
      'Rithmo helps organizations monitor external partners (consultants, agencies, legal teams, and implementation vendors), surfacing delivery risks, unresolved commitments, and slowing momentum before they become expensive problems.',
  })

  return (
    <div className="min-h-screen bg-white">
      <Navbar transparent />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background: subtle asymmetric gradient — distinct from other pages */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/60 via-white to-accent-50/30" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-primary-500/8 rounded-full blur-3xl animate-pulse-slow" />
        <div
          className="absolute bottom-0 left-10 w-80 h-80 bg-accent-500/10 rounded-full blur-3xl animate-pulse-slow"
          style={{ animationDelay: '3s' }}
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Copy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-5">
                <span className="badge-primary text-sm">For External Engagement Leaders</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Stay Ahead of{' '}
                <span className="gradient-text">External Project Risk.</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
                Rithmo attends the meetings where your external projects are being coordinated,
                surfacing delivery risks, unresolved commitments, and slowing momentum before
                they show up as delays, missed expectations, and expensive surprises.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/contact" className="btn-primary text-lg px-8 py-4 text-center">
                  Book a Demo
                </Link>
              </div>
            </motion.div>

            {/* Visualization */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center lg:justify-end"
            >
              <HeroVisualization />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Warning Signs ────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(ellipse at 30% 50%, rgba(11,111,164,0.2) 0%, transparent 60%)',
          }}
        />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-500/8 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                External Projects Rarely Fail{' '}
                <span className="gradient-text">All at Once.</span>
              </h2>
              <p className="text-lg text-dark-100 leading-relaxed mb-8">
                The warning signs usually appear long before deadlines slip. They show up in meetings,
                week after week, quietly accumulating until the impact is too expensive to ignore.
                By then, the only question is how much it will cost to recover.
              </p>
              <div className="space-y-3">
                {warningSigns.map((sign, i) => (
                  <motion.div
                    key={sign.title}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center text-warning flex-shrink-0 mt-0.5">
                      {sign.icon}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white mb-0.5">{sign.title}</p>
                      <p className="text-xs text-dark-200 leading-relaxed">{sign.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Timeline animation */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
            >
              <p className="text-xs text-dark-300 font-mono uppercase tracking-widest mb-6 text-center lg:text-left">
                A typical external engagement, unmonitored
              </p>
              <WarningSignalTimeline />
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 1.2 }}
                className="text-center lg:text-left text-sm text-dark-300 mt-8 italic"
              >
                Rithmo sees these signals as they appear, not after the deadline slips.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Live Detection ────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Copy */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Rithmo Connects the Pattern.{' '}
                <span className="gradient-text">You Intervene Early.</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Any individual meeting looks normal in isolation. It's the same topic appearing
                in the fourth consecutive meeting, without a clear owner or resolution, that
                becomes a delivery risk. Rithmo tracks across weeks, not just within a single call.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Leaders get a clear picture of which engagements are on track, which are drifting,
                and which require direct intervention, without attending every review themselves.
              </p>
            </motion.div>

            {/* Animated card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
            >
              <LiveEngagementCard />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Capabilities ─────────────────────────────────────────────────── */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-primary-50/20 to-white" />
        <div className="absolute -top-20 right-10 w-72 h-72 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-10 w-80 h-80 bg-primary-500/8 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Intelligence Across Every{' '}
              <span className="gradient-text">External Engagement</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Rithmo gives leaders the visibility they need to understand project health,
              surface risks early, and intervene before small issues become expensive problems.
            </p>
          </motion.div>

          <div className="space-y-20">
            {capabilities.map((cap, i) => (
              <motion.div
                key={cap.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className={`grid lg:grid-cols-2 gap-10 items-center ${
                  i % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={i % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 shadow-glow-sm text-white">
                    {cap.icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {cap.title}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">{cap.description}</p>
                </div>
                <div className={i % 2 === 1 ? 'lg:col-start-1' : ''}>{cap.visual}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industry Verticals ────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Built for Organizations That{' '}
              <span className="gradient-text">Depend on External Expertise</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Whether you're working with consultants, implementation teams, law firms, or
              agencies, Rithmo helps you understand what's happening before it shows up in results.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {verticals.map((vertical, i) => (
              <motion.div
                key={vertical.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl border border-gray-100 p-6 hover:border-accent-500/30 hover:shadow-md transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 shadow-glow-sm text-white group-hover:shadow-glow transition-shadow duration-300">
                  {vertical.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{vertical.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{vertical.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Outcomes ─────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Earlier Visibility.{' '}
                <span className="gradient-text">Better Decisions. Fewer Surprises.</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Rithmo helps organizations understand project health before problems show up in
                deadlines, budgets, and results. The goal isn't to manage projects. It's to give
                leaders the intelligence to know where to focus and when to act.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-4">
              {outcomes.map((outcome, i) => (
                <motion.div
                  key={outcome}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-start gap-3 bg-gray-50 rounded-xl p-4"
                >
                  <CheckCircle className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-gray-800 leading-snug">{outcome}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            background:
              'radial-gradient(ellipse at 70% 50%, rgba(22,193,178,0.18) 0%, transparent 60%)',
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Zero Disruption.{' '}
              <span className="gradient-text">Continuous Intelligence.</span>
            </h2>
            <p className="text-lg text-dark-100 max-w-2xl mx-auto">
              Rithmo fits into existing workflows without changing how your teams or external
              partners operate. Meetings continue as normal. Rithmo handles the rest.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
            {workflowSteps.map((step, i) => (
              <div key={step.label} className="flex md:flex-col items-center gap-4 md:gap-0">
                <div className="flex md:flex-col items-center md:w-full">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.12 }}
                    className="w-14 h-14 rounded-2xl bg-gradient-primary flex items-center justify-center flex-shrink-0 text-white shadow-glow-sm"
                  >
                    {step.icon}
                  </motion.div>
                  {i < workflowSteps.length - 1 ? (
                    <div className="hidden md:flex items-center justify-center w-full my-4">
                      <ArrowRight className="w-4 h-4 text-dark-400" />
                    </div>
                  ) : (
                    <div className="hidden md:block w-full my-4 h-4" />
                  )}
                </div>
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12 + 0.08 }}
                  className="text-sm font-medium text-dark-200 text-center leading-snug md:mt-2 hidden md:block"
                >
                  {step.label}
                </motion.p>
                <p className="text-sm font-medium text-dark-200 leading-snug md:hidden">
                  {step.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Security ─────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary-500/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-600 mb-4">
              Security and Trust
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built to Pass the{' '}
              <span className="gradient-text">Procurement Review</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Organizational intelligence is only valuable when it's trusted. Rithmo is designed
              to satisfy your security team before you ever deploy it.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Shield className="w-5 h-5" />,
                title: 'Data Never Sold or Shared',
                body: 'Meeting content belongs to your organization. Rithmo does not sell, license, or share your data with any third party under any circumstances.',
              },
              {
                icon: <Lock className="w-5 h-5" />,
                title: 'Encrypted in Transit and at Rest',
                body: 'All data is encrypted using AES-256 at rest and TLS 1.3 in transit. Content is never stored as raw recordings.',
              },
              {
                icon: <EyeOff className="w-5 h-5" />,
                title: 'No Recording Retention',
                body: 'Rithmo analyzes meetings for patterns and signals. Raw audio is processed in flight and never written to permanent storage.',
              },
              {
                icon: <FileText className="w-5 h-5" />,
                title: 'Compliance Minded by Design',
                body: 'Built with SOC 2 principles in mind. Data processing agreements and security documentation are available upon request.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-accent-500/30 hover:shadow-md transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center text-white mb-4 shadow-glow-sm">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative glass rounded-3xl p-12 text-center overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-primary opacity-5" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-accent-500/20 rounded-full blur-3xl" />

            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Your External Partners Are Meeting Right Now.
                <br />
                <span className="gradient-text">Do You Know How It's Going?</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto leading-relaxed">
                Rithmo gives leaders the visibility they need to stay ahead of external project risk
                without attending every meeting themselves. Join the waitlist or book a demo.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                  Book a Demo
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common Questions
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card divide-y divide-gray-100"
          >
            {faqs.map((faq) => (
              <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ExternalEngagementsPage
